/**
 * Version history for a message template or a shared layout. Lists the stored
 * versions the server returns, with provenance (where the content came from,
 * and when an author took over a platform default). "Restore" loads a stored
 * version into the editor as UNSAVED work; nothing is written until the author
 * saves a draft (the contract has no server restore).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Btn, InlineEmptyState, Modal, Skeleton, Spinner, StatusPill } from "@v0y4ge/jrni-ui";
import { classifyFailure, type Failure } from "../api";
import { versionWhen, type Tone, type Tr } from "../model";
import { FailureBanner } from "./common";

export interface HistoryRow {
  version: number;
  state: "draft" | "published" | "superseded";
  origin?: "seed" | "authored";
  name?: string;
  createdAt: string;
  createdBy: string | null;
  note: string | null;
  publishedAt: string | null;
}

export interface HistoryModalProps {
  t: Tr;
  open: boolean;
  onClose: () => void;
  subject: string;
  /** Provenance lines, already worded (shown above the list). */
  provenance: ReactNode;
  /** Reads the versions; called each time the modal opens. */
  load: () => Promise<HistoryRow[]>;
  /** Loads one version into the editor as unsaved work. Resolves null on success, or the reason it was refused. */
  onRestore: ((version: number) => Promise<string | null>) | null;
}

const STATE_TONE: Record<HistoryRow["state"], Tone> = { draft: "neutral", published: "success", superseded: "neutral" }; // ui-config

export function HistoryModal({ t, open, onClose, subject, provenance, load, onRestore }: HistoryModalProps) {
  const [state, setState] = useState<{ phase: "loading" | "ready" | "error"; rows: HistoryRow[]; failure: Failure | null }>({ phase: "loading", rows: [], failure: null });
  const [busy, setBusy] = useState<number | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const seq = useRef(0);
  const [nonce, setNonce] = useState(0);
  const loadRef = useRef(load); loadRef.current = load;

  useEffect(() => {
    if (!open) return;
    const mine = ++seq.current;
    setRefusal(null);
    setState((s) => ({ phase: "loading", rows: s.rows, failure: null }));
    loadRef.current().then(
      (rows) => { if (seq.current === mine) setState({ phase: "ready", rows, failure: null }); },
      (e: unknown) => { if (seq.current === mine) setState({ phase: "error", rows: [], failure: classifyFailure(e) }); },
    );
  }, [open, nonce]);

  const stateLabel = (s: HistoryRow["state"]) => s === "published" ? t("messaging.builder.history.published", "Sending") : s === "draft" ? t("messaging.builder.history.draft", "Draft") : t("messaging.builder.history.superseded", "Earlier version");
  const originLabel = (o: HistoryRow["origin"]) => o === "seed" ? t("messaging.builder.history.platform", "Platform default") : o === "authored" ? t("messaging.builder.history.authored", "Written by your team") : null;

  return (
    <Modal open={open} onClose={onClose} title={t("messaging.builder.history", "Version history")} description={subject} maxWidth={640}>
      <Modal.Body>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-3)" }}>
          {provenance}
          {refusal ? <p className="msgb-fielderr" role="alert">{refusal}</p> : null}
          {state.phase === "loading" && !state.rows.length ? (
            <div role="status" aria-live="polite" aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-2)" }}>
              <span className="msgb-sr">{t("messaging.builder.history.loading", "Loading version history")}</span>
              {[0, 1, 2].map((i) => (
                <div key={i} className="msgb-version">
                  <div style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-2)", minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "var(--jrni-space-2)" }}>
                      <Skeleton variant="text" width="40%" height={14} />
                      <Skeleton variant="rect" width={60} height={20} />
                      <Skeleton variant="rect" width={60} height={20} />
                    </div>
                    <Skeleton variant="text" width="30%" height={12} />
                  </div>
                  {onRestore ? <Skeleton variant="rect" width={120} height={28} /> : null}
                </div>
              ))}
            </div>
          ) : null}
          {state.phase === "error" && state.failure ? <FailureBanner t={t} failure={state.failure} onRetry={() => setNonce((n) => n + 1)} /> : null}
          {state.phase === "ready" && !state.rows.length ? (
            <InlineEmptyState size="sm" title={t("messaging.builder.history.none", "No versions yet")}
              description={t("messaging.builder.history.noneText", "Versions appear here once a draft is saved or published.")} />
          ) : null}
          {state.rows.length ? (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }} aria-busy={state.phase === "loading"}>
              {state.rows.map((v, i) => (
                <li key={v.version} className="msgb-version">
                  <div style={{ display: "flex", flexDirection: "column", gap: "var(--jrni-space-1)", minWidth: 0 }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--jrni-space-2)", alignItems: "center" }}>
                      <span className="msgb-vtitle">{t("messaging.builder.history.version", "Version")} {v.version}{v.name ? ` · ${v.name}` : ""}</span>
                      <StatusPill tone={STATE_TONE[v.state]} size="sm">{stateLabel(v.state)}</StatusPill>
                      {/* the origin is said where it changes, not repeated on every version */}
                      {originLabel(v.origin) && (i === 0 || state.rows[i - 1].origin !== v.origin) ? <StatusPill tone="neutral" size="sm">{originLabel(v.origin)}</StatusPill> : null}
                    </div>
                    <span className="msgb-small">{versionWhen(v.publishedAt || v.createdAt)}</span>
                    {v.createdBy ? <span className="msgb-small" style={{ overflowWrap: "anywhere" }}>{v.createdBy}</span> : null}
                    {v.note ? <span className="msgb-small">{v.note}</span> : null}
                  </div>
                  {onRestore ? (
                    <Btn size="sm" variant="secondary" disabled={busy !== null} aria-busy={busy === v.version}
                      icon={busy === v.version ? <Spinner size="sm" label={t("messaging.builder.working", "Working")} /> : undefined}
                      onClick={async () => {
                        setBusy(v.version); setRefusal(null);
                        const reason = await onRestore(v.version);
                        setBusy(null);
                        if (reason) setRefusal(reason); else onClose();
                      }}>
                      {t("messaging.builder.history.restore", "Load into editor")}
                    </Btn>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          {onRestore ? <p className="msgb-small">{t("messaging.builder.history.restoreText", "Loading a version puts it in the editor as unsaved changes. Save a draft, then publish, to send it.")}</p> : null}
        </div>
      </Modal.Body>
    </Modal>
  );
}
